/**
 * Request ids and error reports for the admin (backend lib/observability, docs/OBSERVABILITY.md).
 *
 *  - Every call to the API gets an x-request-id (the backend keeps it, puts it in every log line
 *    of that request and sends it back), so "it failed" can be matched to the server's logs.
 *    The last failed call's id is remembered, and error messages for server errors end with
 *    "Reference: <id>" (lib/sdk.ts errorMessage).
 *  - Errors in the admin itself (a page that crashed, an unhandled rejection from our own code)
 *    are sent to POST /admin/terify/me/client-errors: at most 5 per page load, each message once,
 *    no query strings, ids in the page address replaced. System status → Recent errors shows them.
 *
 * Nothing here may break the admin: every step is wrapped, and the original fetch is used
 * whenever something unexpected comes up.
 */

import { IDLE_HEADER, isIdle } from "./activity"

const API_PATH = /^\/(admin|auth|store)(\/|$)/
const REPORT_PATH = "/admin/terify/me/client-errors"

export type FailedCall = { id: string; status: number; path: string; at: number }
let lastFailure: FailedCall | null = null

export function newRequestId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`
  }
}

/** The id of the API call that failed last with a server error (or network error), if it was within `withinMs`. */
export function lastFailedCall(withinMs = 15_000): FailedCall | null {
  return lastFailure && Date.now() - lastFailure.at <= withinMs ? lastFailure : null
}

/** "/orders/order_01J…/edit?tab=x" → "/orders/:id/edit". */
export function routeTemplate(pathname: string): string {
  return (pathname.split(/[?#]/)[0] || "/")
    .split("/")
    .map((s) => (/^[a-z][a-z0-9]{1,15}_[0-9A-Za-z]{10,}$/.test(s) ? ":id" : /^\d+$/.test(s) ? ":n" : s.length >= 20 && /\d/.test(s) && /^[\w.~-]+$/.test(s) ? ":token" : s))
    .join("/")
}

function apiOrigin(): string {
  try {
    return new URL(import.meta.env.VITE_MEDUSA_BACKEND_URL || window.location.origin, window.location.href).origin
  } catch {
    return window.location.origin
  }
}

let installed = false

/** Wraps window.fetch for API calls. Call once, before anything fetches. */
export function installRequestIds() {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return
  installed = true
  const original = window.fetch.bind(window)
  const origin = apiOrigin()
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    let id: string | null = null
    let path = ""
    let args: [RequestInfo | URL, RequestInit | undefined] = [input, init]
    try {
      const url = new URL(typeof input === "string" ? input : input instanceof URL ? input.href : input.url, window.location.href)
      if (url.origin === origin && API_PATH.test(url.pathname)) {
        path = url.pathname
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
        id = headers.get("x-request-id") || newRequestId()
        headers.set("x-request-id", id)
        // Calls made while nobody uses the tab don't keep the sign-in alive (lib/activity.ts).
        if (isIdle()) headers.set(IDLE_HEADER, "1")
        args = input instanceof Request && !init ? [new Request(input, { headers }), undefined] : [input, { ...init, headers }]
      }
    } catch {
      id = null
      args = [input, init]
    }
    if (!id) return original(input, init)
    try {
      const res = await original(...args)
      try {
        if (res.status >= 500) lastFailure = { id: res.headers.get("x-request-id") || id, status: res.status, path, at: Date.now() }
      } catch {}
      return res
    } catch (e) {
      lastFailure = { id, status: 0, path, at: Date.now() }
      throw e
    }
  } as typeof window.fetch
}

// ── error reports ────────────────────────────────────────────────────────────

const MAX_REPORTS = 5
let sent = 0
const seen = new Set<string>()

/** Noise that isn't a bug in the admin: browser quirks, extensions, aborted fetches, API errors (logged by the server). */
export function ignorable(e: unknown, message: string): boolean {
  if (!message) return true
  if (/ResizeObserver loop|^Script error\.?$|Non-Error promise rejection|AbortError|The user aborted|signal is aborted/i.test(message)) return true
  if (/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk/i.test(message)) return true
  if (e && typeof e === "object" && typeof (e as { status?: unknown }).status === "number") return true
  return false
}

export function reportClientError(e: unknown, where = "window"): void {
  try {
    const err = e instanceof Error ? e : null
    const message = String(err?.message ?? (typeof e === "string" ? e : "") ?? "").slice(0, 2000)
    if (ignorable(e, message) || sent >= MAX_REPORTS) return
    const key = `${err?.name}:${message}`
    if (seen.has(key)) return
    seen.add(key)
    sent++
    const failed = lastFailedCall()
    const body = {
      kind: err?.name || "Error",
      message,
      stack: String(err?.stack ?? "").slice(0, 8000),
      route: `${where} ${routeTemplate(window.location.pathname)}`,
      path: routeTemplate(window.location.pathname),
      request_id: (e as { requestId?: string } | null)?.requestId ?? failed?.id ?? undefined,
    }
    void window
      .fetch(apiOrigin() + REPORT_PATH, { method: "POST", credentials: "include", keepalive: true, headers: { "content-type": "application/json" }, body: JSON.stringify(body) })
      .catch(() => undefined)
  } catch {
    // never
  }
}

/** Unhandled errors and rejections from the admin's own scripts. */
export function installErrorReports() {
  if (typeof window === "undefined") return
  try {
    window.addEventListener("error", (ev) => {
      // Only our own scripts (extensions and third-party frames report "Script error." or other files).
      if (ev.filename && !ev.filename.startsWith(window.location.origin)) return
      reportClientError(ev.error ?? ev.message, "window")
    })
    window.addEventListener("unhandledrejection", (ev) => reportClientError(ev.reason, "promise"))
  } catch {}
}
